<template>
  <el-button
    class="ui-button"
    :type="elementType"
    :class="[
      `ui-button--${effectiveVariant}`,
      `ui-button--tone-${effectiveTone}`,
      { 'is-active': active, 'is-block': block, 'is-icon-only': iconOnly, 'has-attention': attention }
    ]"
    :disabled="disabled"
    :loading="loading"
    :native-type="nativeType"
    :data-ui-action="action || null"
    v-bind="$attrs"
    :icon="resolvedIcon"
    v-on="$listeners"
  >
    <FireParticleAura
      v-if="showDirtyParticles"
      :key="particleEffectRevision"
      :particle-id="activeParticleId"
      class="ui-button-fire-particles"
      :options="fireParticleOptions"
      :anchor="$el"
    />
    <slot />
  </el-button>
</template>

<script>
import FireParticleAura from '@/components/FireParticleAura'
import { getThemeColor } from '@/utils/theme'

let buttonParticleSequence = 0

export default {
  name: 'UiButton',
  components: { FireParticleAura },
  inheritAttrs: false,
  props: {
    // 兼容原有 el-button 的 type，业务页面迁移时不改变按钮语义与 Element 行为。
    type: {
      type: String,
      default: '',
      validator: value => ['', 'primary', 'success', 'warning', 'danger', 'info', 'text'].includes(value)
    },
    variant: {
      type: String,
      default: 'standard',
      validator: value => ['standard', 'text', 'control', 'device'].includes(value)
    },
    tone: {
      type: String,
      default: '',
      validator: value => ['', 'primary', 'warning', 'danger', 'accent', 'neutral'].includes(value)
    },
    active: { type: Boolean, default: false },
    attention: { type: Boolean, default: false },
    block: { type: Boolean, default: false },
    iconOnly: { type: Boolean, default: false },
    disabled: { type: Boolean, default: false },
    loading: { type: Boolean, default: false },
    nativeType: { type: String, default: 'button' },
    action: {
      type: String,
      default: '',
      validator: value => ['', 'open-create', 'create', 'save', 'confirm', 'cancel', 'close', 'delete', 'edit', 'refresh', 'reset', 'back', 'search'].includes(value)
    }
  },
  data () {
    buttonParticleSequence += 1
    return {
      dirtyEffectActive: false,
      particleId: `ui-button-fire-${buttonParticleSequence}`,
      particleEffectRevision: 0,
      particleEffectTimer: null,
      fireParticleOptions: {
        fullScreen: { enable: false },
        background: { color: 'transparent' },
        detectRetina: true,
        fpsLimit: 30,
        particles: {
          color: { value: [getThemeColor('particleCore'), getThemeColor('particleWarm')] },
          move: {
            enable: true,
            direction: 'top',
            random: true,
            speed: { min: 0.45, max: 1.35 },
            straight: false,
            outModes: { default: 'out', bottom: 'out', top: 'out' }
          },
          number: { value: 15, density: { enable: false } },
          opacity: {
            value: { min: 0.25, max: 0.85 },
            animation: { enable: true, speed: 1.8, sync: false, startValue: 'max', destroy: 'min' }
          },
          shape: { type: 'circle' },
          shadow: {
            enable: true,
            color: getThemeColor('particleGlow'),
            blur: 10,
            offset: { x: 0, y: 0 }
          },
          size: {
            value: { min: 1, max: 3.8 },
            animation: { enable: true, speed: 2.2, sync: false, startValue: 'random', destroy: 'min' }
          }
        }
      }
    }
  },
  computed: {
    showDirtyParticles () {
      return this.attention || (this.dirtyEffectActive && ['create', 'save'].includes(this.action))
    },
    activeParticleId () {
      return `${this.particleId}-${this.particleEffectRevision}`
    },
    resolvedIcon () {
      const icons = {
        'open-create': 'el-icon-plus',
        create: 'el-icon-plus',
        save: 'el-icon-check',
        confirm: 'el-icon-check',
        cancel: 'el-icon-close',
        close: 'el-icon-close',
        delete: 'el-icon-delete',
        edit: 'el-icon-edit',
        refresh: 'el-icon-refresh',
        reset: 'el-icon-refresh-left',
        back: 'el-icon-back',
        search: 'el-icon-search'
      }
      return icons[this.action] || this.$attrs.icon || ''
    },
    effectiveVariant () {
      return this.type === 'text' ? 'text' : this.variant
    },
    effectiveTone () {
      // 普通刷新属于中性操作，全站保持深色底、浅色文字的统一视觉。
      // 删除后重载、重试等业务动作不使用 refresh action，仍由调用方保留警告色。
      if (['refresh', 'cancel', 'close'].includes(this.action)) return 'neutral'
      if (this.action === 'edit') return 'warning'
      if (this.tone) return this.tone
      if (this.action === 'reset') return 'neutral'
      if (this.type === 'warning') return 'warning'
      if (this.type === 'danger') return 'danger'
      if (this.type === 'success') return 'primary'
      if (this.type === 'info') return 'neutral'
      if (!this.type) return 'primary'
      return 'primary'
    },
    elementType () {
      // type 只作为兼容语义输入，视觉统一由 UiButton 自身的 variant/tone 控制。
      return this.effectiveVariant === 'text' ? 'text' : undefined
    }
  },
  mounted () {
    window.addEventListener('ui-form-dirty-change', this.handleDirtyEffectChange)
  },
  beforeDestroy () {
    window.removeEventListener('ui-form-dirty-change', this.handleDirtyEffectChange)
    if (this.particleEffectTimer) window.clearTimeout(this.particleEffectTimer)
  },
  methods: {
    handleDirtyEffectChange (event) {
      const scope = event.detail && event.detail.scope
      if (!scope || !scope.contains(this.$el)) return
      const dirty = Boolean(event.detail.dirty)
      if (!dirty) {
        if (this.particleEffectTimer) window.clearTimeout(this.particleEffectTimer)
        this.particleEffectTimer = null
        this.dirtyEffectActive = false
        return
      }

      if (!this.dirtyEffectActive) {
        this.dirtyEffectActive = true
        this.particleEffectRevision += 1
        return
      }

      // 同一次输入可能连续触发 input/change/click；合并后只重启一次粒子实例。
      if (this.particleEffectTimer) window.clearTimeout(this.particleEffectTimer)
      this.particleEffectTimer = window.setTimeout(() => {
        this.particleEffectTimer = null
        if (this.dirtyEffectActive) this.particleEffectRevision += 1
      }, 420)
    }
  }
}
</script>

<style lang="scss">
.ui-button {
  position: relative;
  isolation: isolate;
  box-sizing: border-box;
  color: var(--ui-text-primary);
  border-color: var(--ui-border-color);
  border-radius: var(--ui-radius-medium);
  background: var(--ui-bg-panel-elevated);
  font-weight: var(--ui-font-weight-medium);
  cursor: pointer;
  box-shadow: var(--ui-shadow-control);
  transition: color var(--ui-duration-fast) ease,
    border-color var(--ui-duration-fast) ease,
    background-color var(--ui-duration-fast) ease,
    opacity var(--ui-duration-fast) ease,
    box-shadow var(--ui-duration-fast) ease,
    transform var(--ui-duration-normal) var(--ui-ease-release);
}

/* 表单发生改动后，仅强调负责提交数据的主操作按钮。 */
.has-unsaved-changes .ui-button[data-ui-action="create"],
.has-unsaved-changes .ui-button[data-ui-action="save"] {
  border-color: rgba(var(--ui-color-warning-rgb), .95);
  box-shadow: inset 0 0 8px rgba(var(--ui-color-warning-rgb), .16),
    0 0 14px rgba(var(--ui-color-warning-rgb), .3);
  animation: ui-form-corona-pulse 1.7s ease-in-out infinite;
}

.ui-button.has-attention {
  border-color: rgba(var(--ui-color-warning-rgb), .95);
  box-shadow: inset 0 0 8px rgba(var(--ui-color-warning-rgb), .16),
    0 0 14px rgba(var(--ui-color-warning-rgb), .3);
  animation: ui-form-corona-pulse 1.7s ease-in-out infinite;
}

.ui-button-fire-particles {
  position: fixed;
  z-index: 3000;
  overflow: visible;
  pointer-events: none;
  /* 中心区域留空，粒子只在按钮四周出现，不会覆盖按钮文字和图标。 */
  -webkit-mask:
    linear-gradient(var(--ui-color-black) 0 0) top / 100% 30px no-repeat,
    linear-gradient(var(--ui-color-black) 0 0) bottom / 100% 10px no-repeat,
    linear-gradient(var(--ui-color-black) 0 0) left 30px / 24px calc(100% - 40px) no-repeat,
    linear-gradient(var(--ui-color-black) 0 0) right 30px / 24px calc(100% - 40px) no-repeat;
  mask:
    linear-gradient(var(--ui-color-black) 0 0) top / 100% 30px no-repeat,
    linear-gradient(var(--ui-color-black) 0 0) bottom / 100% 10px no-repeat,
    linear-gradient(var(--ui-color-black) 0 0) left 30px / 24px calc(100% - 40px) no-repeat,
    linear-gradient(var(--ui-color-black) 0 0) right 30px / 24px calc(100% - 40px) no-repeat;
}

.ui-button-fire-particles canvas {
  position: absolute;
  inset: 0;
  width: 100% !important;
  height: 100% !important;
}

.ui-button > i,
.ui-button > span {
  position: relative;
}

@keyframes ui-form-corona-pulse {
  0%, 100% {
    box-shadow: inset 0 0 6px rgba(var(--ui-color-warning-rgb), .12),
      0 0 10px rgba(var(--ui-color-warning-rgb), .22);
  }
  50% {
    box-shadow: inset 0 0 12px rgba(var(--ui-color-warning-rgb), .24),
      0 0 25px rgba(var(--ui-color-warning-rgb), .48);
  }
}

@media (prefers-reduced-motion: reduce) {
  .has-unsaved-changes .ui-button[data-ui-action="create"],
  .has-unsaved-changes .ui-button[data-ui-action="save"] {
    animation: none;
  }
  .ui-button.has-attention { animation: none; }
}

.ui-button + .ui-button {
  margin-left: var(--ui-control-gap);
}

/* 飞控/外设按钮由容器的 grid/flex gap 排列，不能再叠加相邻按钮边距。 */
.ui-button + .ui-button.ui-button--control,
.ui-button + .ui-button.ui-button--device {
  margin-left: 0;
}

.ui-button:hover:not(.is-disabled) {
  color: var(--ui-text-inverse);
  border-color: var(--ui-color-primary);
  background: rgba(var(--ui-color-primary-rgb), .12);
  box-shadow: 0 0 0 3px rgba(var(--ui-color-primary-rgb), .08), 0 8px 20px rgba(var(--ui-color-black-rgb), .16);
}

.ui-button:active:not(.is-disabled):not(.is-loading) {
  box-shadow: none;
  transform: translateY(2px) scale(.94);
  transition-duration: var(--ui-duration-press);
}

.ui-button:focus:not(:focus-visible) {
  outline: none;
}

.ui-button:focus-visible:not(.is-disabled) {
  outline: 2px solid rgba(var(--ui-color-primary-rgb), .86);
  outline-offset: 2px;
}

.ui-button.is-active {
  color: var(--ui-text-inverse);
  border-color: rgba(var(--ui-color-primary-rgb), .58);
  background: rgba(var(--ui-color-primary-rgb), .1);
}

.ui-button.is-disabled,
.ui-button.is-disabled:hover,
.ui-button.is-disabled:focus-visible {
  color: var(--ui-text-primary);
  border-color: var(--ui-border-color);
  background: var(--ui-bg-surface);
  opacity: .35;
  cursor: not-allowed;
}

/* Element 的白色 loading 蒙层叠加禁用透明度，会把深浅皮肤的文字一起冲淡。 */
.ui-button.is-loading.is-disabled { opacity: 1; }
.ui-button.is-loading::before { background-color: transparent; }

/* 图标承载语义色，文字保持正文色；禁用时图标与文字统一弱化。 */
.ui-button:not(.is-disabled):not(.is-icon-only):not([data-ui-action="save"]):not([data-ui-action="confirm"]):not([data-ui-action="create"]) > span > i ~ span {
  color: var(--ui-text-primary);
}
.ui-button.is-disabled i {
  color: var(--ui-text-disabled) !important;
}

.ui-button.is-block {
  width: 100%;
}

.ui-button--standard {
  min-width: 76px;
  height: var(--ui-control-height) !important;
  min-height: var(--ui-control-height) !important;
  padding: 0 var(--ui-space-md);
  font-size: var(--ui-font-size-button) !important;
  line-height: calc(var(--ui-control-height) - 2px);
}

/* 独立按钮圆角不被后加载的 Element 默认 4px 规则覆盖。 */
.ui-button.ui-button--standard {
  border-radius: var(--ui-radius-medium);
}

.ui-button--standard.is-icon-only {
  width: var(--ui-control-height);
  min-width: var(--ui-control-height);
  padding: 0;
}

.ui-button--standard.ui-button--tone-primary {
  color: var(--ui-text-inverse);
  border-color: var(--ui-color-primary);
  background: rgba(var(--ui-color-primary-rgb), .12);
}

.ui-button--standard.ui-button--tone-warning {
  color: var(--ui-text-inverse);
  border-color: var(--ui-color-warning);
  background: rgba(var(--ui-color-warning-rgb), .12);
}

.ui-button--standard.ui-button--tone-danger {
  color: var(--ui-text-inverse);
  border-color: var(--ui-color-danger);
  background: rgba(var(--ui-color-danger-rgb), .12);
}

.ui-button--standard.ui-button--tone-accent {
  color: var(--ui-text-inverse);
  border-color: var(--ui-color-accent);
  background: rgba(var(--ui-color-accent-rgb), .12);
}

.ui-button--tone-accent:hover:not(.is-disabled) {
  color: var(--ui-text-inverse);
  border-color: var(--ui-color-accent-hover);
  background: rgba(var(--ui-color-accent-rgb), .18);
}

.ui-button--tone-accent:active:not(.is-disabled) {
  border-color: var(--ui-color-accent-active);
  background: rgba(var(--ui-color-accent-rgb), .24);
}

.ui-button--standard.ui-button--tone-neutral {
  color: var(--ui-text-inverse);
  border-color: var(--ui-text-secondary);
  background: var(--ui-bg-elevated);
}

.ui-button--standard.ui-button--tone-primary:hover:not(.is-disabled) {
  border-color: var(--ui-color-primary-hover);
  background: rgba(var(--ui-color-primary-rgb), .18);
}

/* AXIS 主操作只迁移普通提交/确认按钮，飞控、开关和状态语义保留。 */
.ui-button.ui-button--standard.ui-button--tone-primary:not(.is-disabled) {
  &[data-ui-action='create'],
  &[data-ui-action='save'],
  &[data-ui-action='confirm'] {
    --ui-submit-border: var(--ui-action-primary);
    --ui-submit-hover-border: var(--ui-action-primary-hover);
    --ui-submit-icon: var(--ui-action-primary-ink);
    color: var(--ui-action-primary-ink);
    border-color: var(--ui-submit-border);
    background: var(--ui-action-primary);

    i { color: var(--ui-submit-icon); }

    &:hover {
      color: var(--ui-action-primary-ink);
      border-color: var(--ui-submit-hover-border);
      background: var(--ui-action-primary-hover);
      box-shadow: var(--ui-shadow-control);
    }

    &:active, &.is-active {
      background: var(--ui-action-primary-active);
      i { color: var(--ui-submit-icon); }
    }

    &:focus-visible { outline-color: var(--ui-action-primary); }

    &:focus:not(:focus-visible):not(.is-active):not(:active) {
      color: var(--ui-action-primary-ink);
      border-color: var(--ui-submit-border);
      background: var(--ui-action-primary);
      box-shadow: var(--ui-shadow-control);
    }

    @media (hover: none), (pointer: coarse) {
      &:hover:not(.is-active):not(:active) {
        color: var(--ui-action-primary-ink);
        border-color: var(--ui-submit-border);
        background: var(--ui-action-primary);
        box-shadow: var(--ui-shadow-control);
        i { color: var(--ui-submit-icon); }
      }
    }

    .has-unsaved-changes &, &.has-attention {
      --ui-submit-border: var(--ui-color-warning);
      --ui-submit-hover-border: var(--ui-color-warning-hover);
      --ui-submit-icon: var(--ui-color-warning-active);
    }
  }
}

.ui-button--standard.ui-button--tone-neutral:hover:not(.is-disabled) {
  border-color: var(--ui-text-primary);
  background: rgba(var(--ui-color-white-rgb), .08);
}

.ui-button--standard.ui-button--tone-warning:hover:not(.is-disabled) {
  color: var(--ui-text-inverse);
  border-color: var(--ui-color-warning-hover);
  background: rgba(var(--ui-color-warning-rgb), .16);
}

.ui-button--standard.ui-button--tone-danger:hover:not(.is-disabled) {
  color: var(--ui-text-inverse);
  border-color: var(--ui-color-danger-hover);
  background: rgba(var(--ui-color-danger-rgb), .16);
}

.ui-button.ui-button--text {
  min-width: 0;
  min-height: 0;
  padding: var(--ui-space-xs);
  color: var(--ui-color-primary);
  border-color: transparent;
  background: transparent;
  box-shadow: none;
  font-size: var(--ui-font-size-button) !important;
}

.ui-button.ui-button--text.ui-button--tone-warning {
  color: var(--ui-color-warning);
}

.ui-button.ui-button--text.ui-button--tone-danger {
  color: var(--ui-color-danger);
}

.ui-button.ui-button--text.ui-button--tone-accent {
  color: var(--ui-color-accent);
}

.ui-button.ui-button--text.ui-button--tone-neutral {
  color: var(--ui-text-secondary);
}

.ui-button--text:hover:not(.is-disabled) {
  color: var(--ui-color-primary-hover);
  border-color: transparent;
  background: transparent;
  box-shadow: none;
}

.ui-button--text.ui-button--tone-warning:hover:not(.is-disabled) {
  color: var(--ui-color-warning-hover);
  background: transparent;
}

.ui-button--text.ui-button--tone-danger:hover:not(.is-disabled) {
  color: var(--ui-color-danger-hover);
  background: transparent;
}

.ui-button--text.ui-button--tone-accent:hover:not(.is-disabled) {
  color: var(--ui-color-accent-hover);
  background: transparent;
}

.ui-button--text.ui-button--tone-neutral:hover:not(.is-disabled) {
  color: var(--ui-text-primary);
  background: transparent;
}

.ui-button--text.is-disabled,
.ui-button--text.is-disabled:hover,
.ui-button--text.is-disabled:focus-visible {
  color: var(--ui-text-disabled);
  border-color: transparent;
  background: transparent;
}

.ui-button--control,
.ui-button--control:focus:not(.is-disabled) {
  min-width: 0;
  height: 62px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  color: var(--ui-text-inverse);
  border-radius: var(--ui-radius-medium);
  background: var(--ui-bg-surface);
  font-size: var(--ui-font-size-control-button) !important;
  font-weight: var(--ui-font-weight-bold);
  line-height: 1.2;
}

.ui-button--control.ui-button--tone-primary,
.ui-button--device.ui-button--tone-primary,
.ui-button--control.ui-button--tone-primary:focus:not(.is-disabled),
.ui-button--device.ui-button--tone-primary:focus:not(.is-disabled) {
  border-color: var(--ui-color-primary);
  background: rgba(var(--ui-color-primary-rgb), .09);
}

.ui-button--control.ui-button--tone-warning,
.ui-button--device.ui-button--tone-warning,
.ui-button--control.ui-button--tone-warning:focus:not(.is-disabled),
.ui-button--device.ui-button--tone-warning:focus:not(.is-disabled) {
  border-color: var(--ui-color-warning);
  background: rgba(var(--ui-color-warning-rgb), .09);
}

.ui-button--control.ui-button--tone-danger,
.ui-button--device.ui-button--tone-danger,
.ui-button--control.ui-button--tone-danger:focus:not(.is-disabled),
.ui-button--device.ui-button--tone-danger:focus:not(.is-disabled) {
  border-color: var(--ui-color-danger);
  background: rgba(var(--ui-color-danger-rgb), .09);
}

.ui-button--control.ui-button--tone-accent,
.ui-button--device.ui-button--tone-accent,
.ui-button--control.ui-button--tone-accent:focus:not(.is-disabled),
.ui-button--device.ui-button--tone-accent:focus:not(.is-disabled) {
  border-color: var(--ui-color-accent);
  background: rgba(var(--ui-color-accent-rgb), .09);
}

.ui-button--control.ui-button--tone-neutral,
.ui-button--device.ui-button--tone-neutral,
.ui-button--control.ui-button--tone-neutral:focus:not(.is-disabled),
.ui-button--device.ui-button--tone-neutral:focus:not(.is-disabled) {
  border-color: var(--ui-text-secondary);
}

.ui-button--control.ui-button--tone-primary:hover:not(.is-disabled),
.ui-button--device.ui-button--tone-primary:hover:not(.is-disabled) {
  border-color: var(--ui-color-primary-hover);
  background: rgba(var(--ui-color-primary-rgb), .15);
}

.ui-button--control.ui-button--tone-warning:hover:not(.is-disabled),
.ui-button--device.ui-button--tone-warning:hover:not(.is-disabled) {
  border-color: var(--ui-color-warning-hover);
  background: rgba(var(--ui-color-warning-rgb), .15);
}

.ui-button--control.ui-button--tone-danger:hover:not(.is-disabled),
.ui-button--device.ui-button--tone-danger:hover:not(.is-disabled) {
  border-color: var(--ui-color-danger-hover);
  background: rgba(var(--ui-color-danger-rgb), .15);
}

.ui-button--control.ui-button--tone-accent:hover:not(.is-disabled),
.ui-button--device.ui-button--tone-accent:hover:not(.is-disabled) {
  border-color: var(--ui-color-accent-hover);
  background: rgba(var(--ui-color-accent-rgb), .15);
}

.ui-button--control.ui-button--tone-neutral:hover:not(.is-disabled),
.ui-button--device.ui-button--tone-neutral:hover:not(.is-disabled) {
  border-color: var(--ui-text-primary);
}

.ui-button--control > span {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: var(--ui-space-xs);
}

.ui-button--control i {
  color: var(--ui-color-primary);
  font-size: 21px;
  font-weight: 400;
}

.ui-button--device,
.ui-button--device:focus:not(.is-disabled) {
  min-width: 0;
  height: 52px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 var(--ui-space-sm);
  color: var(--ui-text-inverse);
  border-radius: 5px;
  background: var(--ui-bg-control-elevated);
  font-size: var(--ui-font-size-control-button) !important;
  font-weight: var(--ui-font-weight-bold);
  line-height: 1.2;
}

.ui-button--device > span {
  display: flex;
  min-width: 0;
  align-items: center;
  justify-content: center;
  gap: var(--ui-space-xs);
  white-space: normal;
  line-height: 1.1;
}

.ui-button--device > span > span {
  min-width: 0;
  text-align: center;
}

.ui-button--device i {
  color: var(--ui-color-primary);
  font-size: 17px;
  font-weight: 400;
}

.ui-button--tone-warning i {
  color: var(--ui-color-warning);
}

.ui-button--tone-primary i {
  color: var(--ui-color-primary);
}

.ui-button--tone-accent i {
  color: var(--ui-color-accent);
}

.ui-button--tone-danger i {
  color: var(--ui-color-danger);
}

.ui-button--tone-neutral i {
  color: var(--ui-text-secondary);
}

.ui-button--tone-primary:hover:not(.is-disabled) i {
  color: var(--ui-color-primary-hover);
}

.ui-button--tone-warning:hover:not(.is-disabled) i {
  color: var(--ui-color-warning-hover);
}

.ui-button--tone-danger:hover:not(.is-disabled) i {
  color: var(--ui-color-danger-hover);
}

.ui-button--tone-accent:hover:not(.is-disabled) i {
  color: var(--ui-color-accent-hover);
}

.ui-button--tone-neutral:hover:not(.is-disabled) i {
  color: var(--ui-text-primary);
}

/* 鼠标点击产生的普通 focus 不改变按钮外观；键盘焦点由 focus-visible 单独表达。 */
.ui-button.ui-button--standard.ui-button--tone-primary:focus:not(:focus-visible):not(.is-active):not(:active) {
  color: var(--ui-text-inverse);
  border-color: var(--ui-color-primary);
  background: rgba(var(--ui-color-primary-rgb), .12);
  box-shadow: var(--ui-shadow-control);
}

.ui-button.ui-button--standard.ui-button--tone-warning:focus:not(:focus-visible):not(.is-active) {
  color: var(--ui-text-inverse);
  border-color: var(--ui-color-warning);
  background: var(--ui-bg-panel-elevated);
  box-shadow: var(--ui-shadow-control);
}

.ui-button.ui-button--standard.ui-button--tone-danger:focus:not(:focus-visible):not(.is-active) {
  color: var(--ui-text-inverse);
  border-color: var(--ui-color-danger);
  background: var(--ui-bg-panel-elevated);
  box-shadow: var(--ui-shadow-control);
}

.ui-button.ui-button--standard.ui-button--tone-accent:focus:not(:focus-visible):not(.is-active) {
  color: var(--ui-text-inverse);
  border-color: var(--ui-color-accent);
  background: var(--ui-bg-panel-elevated);
  box-shadow: var(--ui-shadow-control);
}

.ui-button.ui-button--standard.ui-button--tone-neutral:focus:not(:focus-visible):not(.is-active) {
  color: var(--ui-text-inverse);
  border-color: var(--ui-text-secondary);
  background: var(--ui-bg-elevated);
  box-shadow: var(--ui-shadow-control);
}

.ui-button.ui-button--text:focus:not(:focus-visible):not(.is-active) {
  border-color: transparent;
  background: transparent;
  box-shadow: none;
}

.ui-button.ui-button--text.ui-button--tone-primary:focus:not(:focus-visible):not(.is-active) { color: var(--ui-color-primary); }
.ui-button.ui-button--text.ui-button--tone-warning:focus:not(:focus-visible):not(.is-active) { color: var(--ui-color-warning); }
.ui-button.ui-button--text.ui-button--tone-danger:focus:not(:focus-visible):not(.is-active) { color: var(--ui-color-danger); }
.ui-button.ui-button--text.ui-button--tone-accent:focus:not(:focus-visible):not(.is-active) { color: var(--ui-color-accent); }
.ui-button.ui-button--text.ui-button--tone-neutral:focus:not(:focus-visible):not(.is-active) { color: var(--ui-text-secondary); }

.ui-button.ui-button--control:focus:not(:focus-visible):not(.is-active),
.ui-button.ui-button--device:focus:not(:focus-visible):not(.is-active) {
  color: var(--ui-text-inverse);
  box-shadow: var(--ui-shadow-control);
}

/*
 * 触摸设备会在点击后保留合成 hover。这里恢复各语义按钮的默认外观，
 * 持久状态只由 is-active 表达，按压反馈仍由 :active 提供。
 */
@media (hover: none), (pointer: coarse) {
  .ui-button.ui-button--standard.ui-button--tone-primary:hover:not(.is-disabled):not(.is-active):not(:active) {
    color: var(--ui-text-inverse);
    border-color: var(--ui-color-primary);
    background: rgba(var(--ui-color-primary-rgb), .12);
    box-shadow: var(--ui-shadow-control);
  }

  .ui-button.ui-button--standard.ui-button--tone-warning:hover:not(.is-disabled):not(.is-active) {
    color: var(--ui-text-inverse);
    border-color: var(--ui-color-warning);
    background: var(--ui-bg-panel-elevated);
    box-shadow: var(--ui-shadow-control);
  }

  .ui-button.ui-button--standard.ui-button--tone-danger:hover:not(.is-disabled):not(.is-active) {
    color: var(--ui-text-inverse);
    border-color: var(--ui-color-danger);
    background: var(--ui-bg-panel-elevated);
    box-shadow: var(--ui-shadow-control);
  }

  .ui-button.ui-button--standard.ui-button--tone-accent:hover:not(.is-disabled):not(.is-active) {
    color: var(--ui-text-inverse);
    border-color: var(--ui-color-accent);
    background: var(--ui-bg-panel-elevated);
    box-shadow: var(--ui-shadow-control);
  }

  .ui-button.ui-button--standard.ui-button--tone-neutral:hover:not(.is-disabled):not(.is-active) {
    color: var(--ui-text-inverse);
    border-color: var(--ui-text-secondary);
    background: var(--ui-bg-elevated);
    box-shadow: var(--ui-shadow-control);
  }

  .ui-button.ui-button--text:hover:not(.is-disabled):not(.is-active) {
    border-color: transparent;
    background: transparent;
    box-shadow: none;
  }

  .ui-button.ui-button--text.ui-button--tone-primary:hover:not(.is-disabled):not(.is-active) { color: var(--ui-color-primary); }
  .ui-button.ui-button--text.ui-button--tone-warning:hover:not(.is-disabled):not(.is-active) { color: var(--ui-color-warning); }
  .ui-button.ui-button--text.ui-button--tone-danger:hover:not(.is-disabled):not(.is-active) { color: var(--ui-color-danger); }
  .ui-button.ui-button--text.ui-button--tone-accent:hover:not(.is-disabled):not(.is-active) { color: var(--ui-color-accent); }
  .ui-button.ui-button--text.ui-button--tone-neutral:hover:not(.is-disabled):not(.is-active) { color: var(--ui-text-secondary); }

  .ui-button.ui-button--control:hover:not(.is-disabled):not(.is-active) {
    color: var(--ui-text-inverse);
    border-color: var(--ui-border-color);
    background: var(--ui-bg-surface);
    box-shadow: var(--ui-shadow-control);
  }

  .ui-button.ui-button--device:hover:not(.is-disabled):not(.is-active) {
    color: var(--ui-text-inverse);
    border-color: var(--ui-border-color);
    background: var(--ui-bg-control-elevated);
    box-shadow: var(--ui-shadow-control);
  }

  .ui-button.ui-button--control.ui-button--tone-primary:hover:not(.is-disabled):not(.is-active),
  .ui-button.ui-button--device.ui-button--tone-primary:hover:not(.is-disabled):not(.is-active) {
    border-color: var(--ui-color-primary);
    background: rgba(var(--ui-color-primary-rgb), .09);
  }

  .ui-button.ui-button--control.ui-button--tone-warning:hover:not(.is-disabled):not(.is-active),
  .ui-button.ui-button--device.ui-button--tone-warning:hover:not(.is-disabled):not(.is-active) {
    border-color: var(--ui-color-warning);
    background: rgba(var(--ui-color-warning-rgb), .09);
  }

  .ui-button.ui-button--control.ui-button--tone-danger:hover:not(.is-disabled):not(.is-active),
  .ui-button.ui-button--device.ui-button--tone-danger:hover:not(.is-disabled):not(.is-active) {
    border-color: var(--ui-color-danger);
    background: rgba(var(--ui-color-danger-rgb), .09);
  }

  .ui-button.ui-button--control.ui-button--tone-accent:hover:not(.is-disabled):not(.is-active),
  .ui-button.ui-button--device.ui-button--tone-accent:hover:not(.is-disabled):not(.is-active) {
    border-color: var(--ui-color-accent);
    background: rgba(var(--ui-color-accent-rgb), .09);
  }

  .ui-button.ui-button--control.ui-button--tone-neutral:hover:not(.is-disabled):not(.is-active),
  .ui-button.ui-button--device.ui-button--tone-neutral:hover:not(.is-disabled):not(.is-active) {
    border-color: var(--ui-text-secondary);
  }

  .ui-button.ui-button--tone-primary:hover:not(.is-disabled):not(.is-active) i { color: var(--ui-color-primary); }
  .ui-button.ui-button--tone-warning:hover:not(.is-disabled):not(.is-active) i { color: var(--ui-color-warning); }
  .ui-button.ui-button--tone-danger:hover:not(.is-disabled):not(.is-active) i { color: var(--ui-color-danger); }
  .ui-button.ui-button--tone-accent:hover:not(.is-disabled):not(.is-active) i { color: var(--ui-color-accent); }
  .ui-button.ui-button--tone-neutral:hover:not(.is-disabled):not(.is-active) i { color: var(--ui-text-secondary); }

  .ui-button.is-active:hover:not(.is-disabled) {
    color: var(--ui-text-inverse);
    border-color: rgba(var(--ui-color-primary-rgb), .58);
    background: rgba(var(--ui-color-primary-rgb), .1);
  }
}
</style>
